/** Parse a .tsr — full RFC 3161 TimeStampResp OR a bare timestamp token.
 *
 * The Sunlit provenance pipeline stores *bare* CMS ContentInfo tokens under a
 * .tsr extension (rfc3161ng without return_tsr=True), so the sniffer here is
 * load-bearing: outer SEQUENCE whose first child is an OID → bare token;
 * first child is a SEQUENCE (PKIStatusInfo) → full response.
 */

import * as asn1js from "asn1js";
import * as pkijs from "pkijs";

import { toArrayBuffer, toHex } from "./bytes";
import { walkDer } from "./der";
import { hashName, OID_SIGNED_DATA, policyName, WEAK_HASH_OIDS } from "./oids";

/** TSTInfo ::= SEQUENCE { version, policy OBJECT IDENTIFIER, ... } */
function decodeTstPolicyOid(tstBytes: Uint8Array): string | undefined {
  try {
    const root = walkDer(tstBytes);
    const policyNode = root.children[1];
    if (policyNode?.tagName === "OBJECT IDENTIFIER") return policyNode.preview;
  } catch {
    // fall through to pkijs's rendering
  }
  return undefined;
}

export type CheckState = "pass" | "fail" | "skip";

export interface CertSummary {
  subject: string;
  issuer: string;
  serialHex: string;
  notBefore: string; // ISO
  notAfter: string; // ISO
  keyAlgorithm: string;
  sha256Fingerprint: string;
  isTimeStampingEku: boolean;
  ekuCritical: boolean;
  pem: string;
}

export interface TstInfoSummary {
  version: number;
  policyOid: string;
  policyName?: string;
  hashAlgOid: string;
  hashAlgName: string;
  weakHashAlg: boolean;
  messageImprintHex: string;
  serialNumberHex: string;
  genTime: string; // ISO 8601
  accuracy?: { seconds?: number; millis?: number; micros?: number };
  ordering: boolean;
  nonceHex?: string;
  tsaName?: string;
}

export interface StatusSummary {
  code: number;
  text: string;
  statusStrings: string[];
  failInfo?: string;
}

export interface ParsedTsr {
  kind: "tsr" | "token";
  totalBytes: number;
  status?: StatusSummary;
  /** present iff the response contains/is a token */
  tstInfo?: TstInfoSummary;
  certs: CertSummary[];
  signerCertIndex: number | null;
  /** internal handles for verification */
  signedData?: pkijs.SignedData;
  tokenBytes?: Uint8Array;
  /** DER of the TSTInfo (the CMS eContent) */
  tstBytes?: Uint8Array;
}

const PKI_STATUS_TEXT: Record<number, string> = {
  0: "granted",
  1: "granted with modifications",
  2: "rejection",
  3: "waiting",
  4: "revocation warning",
  5: "revocation notification",
};

const FAIL_INFO_BITS: Record<number, string> = {
  0: "badAlg — unrecognized or unsupported algorithm",
  2: "badRequest — transaction not permitted or supported",
  5: "badDataFormat — data submitted has the wrong format",
  14: "timeNotAvailable — the TSA's time source is not available",
  15: "unacceptedPolicy — the requested policy is not supported",
  16: "unacceptedExtension — an extension is not supported",
  17: "addInfoNotAvailable — requested additional info unavailable",
  25: "systemFailure — the request cannot be handled",
};

export function parseTsr(bytes: Uint8Array): ParsedTsr {
  if (bytes.length < 4 || bytes[0] !== 0x30) {
    throw new Error("Not valid DER: file does not start with an ASN.1 SEQUENCE");
  }
  const asn1 = asn1js.fromBER(toArrayBuffer(bytes));
  if (asn1.offset === -1 || !(asn1.result instanceof asn1js.Sequence)) {
    throw new Error("Not valid DER: could not decode the file");
  }
  const first = asn1.result.valueBlock.value[0];

  if (first instanceof asn1js.ObjectIdentifier) {
    // Bare timestamp token (CMS ContentInfo) — the Sunlit fleet's own format.
    const oid = first.valueBlock.toString();
    if (oid !== OID_SIGNED_DATA) {
      throw new Error(
        `File is a CMS structure but not SignedData (content type ${oid})`
      );
    }
    const contentInfo = new pkijs.ContentInfo({ schema: asn1.result });
    return fromToken(contentInfo, bytes, "token", undefined);
  }

  if (first instanceof asn1js.Sequence) {
    // Full TimeStampResp: SEQUENCE { PKIStatusInfo, TimeStampToken OPTIONAL }
    const resp = new pkijs.TimeStampResp({ schema: asn1.result });
    const statusCode = resp.status.status;
    const status: StatusSummary = {
      code: statusCode,
      text: PKI_STATUS_TEXT[statusCode] ?? `unknown (${statusCode})`,
      statusStrings:
        resp.status.statusStrings?.map((s) => s.valueBlock.value) ?? [],
      failInfo: decodeFailInfo(resp.status.failInfo),
    };
    if (!resp.timeStampToken) {
      return {
        kind: "tsr",
        totalBytes: bytes.length,
        status,
        certs: [],
        signerCertIndex: null,
      };
    }
    const tokenBytes = new Uint8Array(resp.timeStampToken.toSchema().toBER());
    return fromToken(resp.timeStampToken, tokenBytes, "tsr", status);
  }

  throw new Error(
    "DER structure is neither a TimeStampResp nor a timestamp token"
  );
}

function fromToken(
  contentInfo: pkijs.ContentInfo,
  tokenBytes: Uint8Array,
  kind: "tsr" | "token",
  status: StatusSummary | undefined
): ParsedTsr {
  const signedData = new pkijs.SignedData({ schema: contentInfo.content });
  const eContent = signedData.encapContentInfo.eContent;
  if (!eContent) {
    throw new Error("Timestamp token has no encapsulated TSTInfo content");
  }
  const tstBytes = new Uint8Array(eContent.getValue());
  const tstAsn1 = asn1js.fromBER(toArrayBuffer(tstBytes));
  if (tstAsn1.offset === -1) {
    throw new Error("Could not decode TSTInfo inside the token");
  }
  const tst = new pkijs.TSTInfo({ schema: tstAsn1.result });

  const certs = (signedData.certificates ?? [])
    .filter((c): c is pkijs.Certificate => c instanceof pkijs.Certificate)
    .map(summarizeCert);

  return {
    kind,
    totalBytes: tokenBytes.length,
    status,
    tstInfo: summarizeTstInfo(tst, tstBytes),
    certs,
    signerCertIndex: findSignerCertIndex(signedData),
    signedData,
    tokenBytes,
    tstBytes,
  };
}

function summarizeTstInfo(
  tst: pkijs.TSTInfo,
  tstBytes: Uint8Array
): TstInfoSummary {
  const hashAlgOid = tst.messageImprint.hashAlgorithm.algorithmId;
  const accuracy = tst.accuracy
    ? {
        seconds: tst.accuracy.seconds,
        millis: tst.accuracy.millis,
        micros: tst.accuracy.micros,
      }
    : undefined;
  // asn1js formats OID arcs beyond Number.MAX_SAFE_INTEGER as "{hex}" —
  // our UUID-derived 2.25 policy arc is one — so re-decode with BigInt.
  const policyOid = tst.policy.includes("{")
    ? decodeTstPolicyOid(tstBytes) ?? tst.policy
    : tst.policy;
  return {
    version: tst.version,
    policyOid,
    policyName: policyName(policyOid),
    hashAlgOid,
    hashAlgName: hashName(hashAlgOid),
    weakHashAlg: WEAK_HASH_OIDS.has(hashAlgOid),
    messageImprintHex: toHex(
      new Uint8Array(tst.messageImprint.hashedMessage.valueBlock.valueHexView)
    ),
    serialNumberHex: toHex(
      new Uint8Array(tst.serialNumber.valueBlock.valueHexView)
    ),
    genTime: tst.genTime.toISOString(),
    accuracy,
    ordering: tst.ordering ?? false,
    nonceHex: tst.nonce
      ? toHex(new Uint8Array(tst.nonce.valueBlock.valueHexView))
      : undefined,
    tsaName: tst.tsa ? generalNameToString(tst.tsa) : undefined,
  };
}

export function summarizeCert(cert: pkijs.Certificate): CertSummary {
  let isTimeStampingEku = false;
  let ekuCritical = false;
  for (const ext of cert.extensions ?? []) {
    if (ext.extnID === "2.5.29.37") {
      const eku = ext.parsedValue as pkijs.ExtKeyUsage | undefined;
      const oids: string[] =
        eku?.keyPurposes ??
        [];
      isTimeStampingEku = oids.includes("1.3.6.1.5.5.7.3.8");
      ekuCritical = ext.critical;
    }
  }
  const der = new Uint8Array(cert.toSchema().toBER());
  return {
    subject: dnToString(cert.subject),
    issuer: dnToString(cert.issuer),
    serialHex: toHex(new Uint8Array(cert.serialNumber.valueBlock.valueHexView)),
    notBefore: cert.notBefore.value.toISOString(),
    notAfter: cert.notAfter.value.toISOString(),
    keyAlgorithm: keyAlgName(cert),
    sha256Fingerprint: "", // filled asynchronously by verify.ts (WebCrypto)
    isTimeStampingEku,
    ekuCritical,
    pem: derToPem(der),
  };
}

const DN_OID_TO_SHORT: Record<string, string> = {
  "2.5.4.3": "CN",
  "2.5.4.6": "C",
  "2.5.4.7": "L",
  "2.5.4.8": "ST",
  "2.5.4.10": "O",
  "2.5.4.11": "OU",
  "1.2.840.113549.1.9.1": "E",
};

export function dnToString(dn: pkijs.RelativeDistinguishedNames): string {
  return dn.typesAndValues
    .map((tv) => {
      const key = DN_OID_TO_SHORT[tv.type] ?? tv.type;
      return `${key}=${tv.value.valueBlock.value}`;
    })
    .join(", ");
}

function generalNameToString(gn: pkijs.GeneralName): string {
  if (gn.type === 4) {
    return dnToString(gn.value as pkijs.RelativeDistinguishedNames);
  }
  if (typeof gn.value === "string") return gn.value;
  return JSON.stringify(gn.value);
}

function keyAlgName(cert: pkijs.Certificate): string {
  const oid = cert.subjectPublicKeyInfo.algorithm.algorithmId;
  if (oid === "1.2.840.10045.2.1") {
    const params = cert.subjectPublicKeyInfo.algorithm.algorithmParams;
    const curveOid =
      params instanceof asn1js.ObjectIdentifier
        ? params.valueBlock.toString()
        : "";
    const curves: Record<string, string> = {
      "1.2.840.10045.3.1.7": "P-256",
      "1.3.132.0.34": "P-384",
      "1.3.132.0.35": "P-521",
    };
    return `ECDSA ${curves[curveOid] ?? curveOid}`.trim();
  }
  if (oid === "1.2.840.113549.1.1.1") return "RSA";
  return oid;
}

function derToPem(der: Uint8Array): string {
  let b64 = "";
  for (let i = 0; i < der.length; i += 0x8000) {
    b64 += String.fromCharCode(...der.subarray(i, i + 0x8000));
  }
  const body = btoa(b64).replace(/(.{64})/g, "$1\n");
  return `-----BEGIN CERTIFICATE-----\n${body}\n-----END CERTIFICATE-----\n`;
}

function findSignerCertIndex(signedData: pkijs.SignedData): number | null {
  const signer = signedData.signerInfos[0];
  if (!signer || !signedData.certificates) return null;
  const sid = signer.sid;
  if (!(sid instanceof pkijs.IssuerAndSerialNumber)) return null;
  const certs = signedData.certificates.filter(
    (c): c is pkijs.Certificate => c instanceof pkijs.Certificate
  );
  for (let i = 0; i < certs.length; i++) {
    const c = certs[i];
    const serialMatch =
      toHex(new Uint8Array(c.serialNumber.valueBlock.valueHexView)) ===
      toHex(new Uint8Array(sid.serialNumber.valueBlock.valueHexView));
    if (serialMatch && c.issuer.isEqual(sid.issuer)) return i;
  }
  return null;
}

function decodeFailInfo(failInfo?: asn1js.BitString): string | undefined {
  if (!failInfo) return undefined;
  const bytes = new Uint8Array(failInfo.valueBlock.valueHexView);
  const unused = failInfo.valueBlock.unusedBits ?? 0;
  const totalBits = bytes.length * 8 - unused;
  const set: string[] = [];
  for (let bit = 0; bit < totalBits; bit++) {
    const byte = bytes[Math.floor(bit / 8)];
    if ((byte >> (7 - (bit % 8))) & 1) {
      set.push(FAIL_INFO_BITS[bit] ?? `bit ${bit}`);
    }
  }
  return set.length ? set.join("; ") : undefined;
}
